<template>
  <v-card class="elevation-1">
    <v-progress-linear v-if="loading" indeterminate color="primary" absolute top></v-progress-linear>

    <template v-if="sheet">
      <v-card-title class="align-start flex-nowrap">
        <v-btn v-if="showBack" icon class="mr-2 ml-n2" @click="$emit('back')">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-chip label color="primary" class="mr-3 font-weight-bold flex-shrink-0">{{ sheet.nummer }}</v-chip>
        <span class="text-break sheet-title">{{ sheet.name }}</span>
      </v-card-title>

      <!-- mt-n2 statt Vuetifys -16px unter dem Titel, sonst kleben die Chips am Namen -->
      <v-card-subtitle class="d-flex flex-wrap align-center chip-gap mt-n2 pb-2">
        <v-chip small outlined>{{ sheet.bund.kurz }}</v-chip>
        <v-chip v-if="sheet.liste === 'B'" small color="indigo" text-color="white">Liste B · {{ sheet.verband.kurz }}</v-chip>
        <v-chip v-else small outlined>Liste A · bundesweit</v-chip>
        <v-chip v-if="sheet.group" small outlined>{{ sheet.group }}</v-chip>
        <v-chip v-if="sheet.detail_gruppe" small outlined>{{ sheet.detail_gruppe }}</v-chip>
        <v-chip v-if="sheet.kategorie" small outlined>{{ sheet.kategorie }}</v-chip>
        <v-chip v-if="sheet.frueher_nummer" small outlined>früher {{ sheet.frueher_nummer }}</v-chip>
        <v-tooltip v-if="sheet.optional" bottom max-width="320">
          <template #activator="{ on, attrs }">
            <v-chip small outlined color="orange" v-bind="attrs" v-on="on">
              <v-icon x-small left>mdi-information-outline</v-icon>Optional
            </v-chip>
          </template>
          Der Verband führt diese Disziplin als optional. Sie wird voraussichtlich aus dem Programm genommen
          (beim BDS z. B. keine Austragung bei der DM).
        </v-tooltip>
        <v-chip v-if="sheet.auslauf_datum" small :color="expired ? 'error' : 'orange'" :outlined="!expired" :text-color="expired ? 'white' : undefined">
          <v-icon x-small left>mdi-calendar-remove</v-icon>
          {{ expired ? "ausgelaufen" : "läuft aus" }}: {{ formatDate(sheet.auslauf_datum) }}
        </v-chip>
        <v-chip small :color="levelColor" text-color="white">
          <v-icon x-small left>mdi-database-check</v-icon>{{ sheet.completeness.label }}
        </v-chip>
      </v-card-subtitle>

      <!-- pt-4: Vuetify nimmt Text direkt unter einem Subtitle sonst den oberen Abstand -->
      <v-card-text class="pt-4">
        <!-- Kennzahlen -->
        <v-row v-if="sheet.key_facts.length" dense>
          <v-col v-for="fact in sheet.key_facts" :key="fact.key" cols="6" sm="4" :lg="factCols">
            <v-sheet outlined rounded class="pa-3 fill-height">
              <div class="text-caption text--secondary">{{ fact.label }}</div>
              <div class="text-subtitle-1 font-weight-medium text--primary text-break">{{ fact.value }}</div>
              <div v-if="fact.derived" class="text-caption text--secondary">aus Bezeichnung abgeleitet</div>
            </v-sheet>
          </v-col>
        </v-row>

        <v-alert v-if="sheet.completeness.level === 1" type="info" text dense class="mt-4 mb-0">
          Für diese Disziplin gibt es bisher nur Grunddaten. Waffe, Ablauf und Scheiben erscheinen hier,
          sobald die Sportordnung des {{ sheet.bund.kurz }} importiert ist.
        </v-alert>

        <!-- Sektionen -->
        <v-card v-for="section in sheet.sections" :key="section.key" outlined class="mt-4">
          <v-card-title class="subtitle-2 grey lighten-4 py-2">
            <v-icon small left>{{ sectionIcon(section.key) }}</v-icon>
            {{ section.title }}
          </v-card-title>

          <v-list v-if="section.type === 'targets' && section.targets.length" dense class="py-0">
            <v-list-item v-for="(target, i) in section.targets" :key="i">
              <v-list-item-icon class="mr-4"><v-icon>mdi-bullseye</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-wrap">{{ target.name }}</v-list-item-title>
                <v-list-item-subtitle class="text-wrap">
                  {{ [target.hersteller, ...target.masse.map(m => `${m.label} ${m.value}`)].filter(Boolean).join(' · ') }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="target.url">
                <v-btn small text color="primary" :href="target.url" target="_blank" rel="noopener">
                  Ansehen <v-icon small right>mdi-open-in-new</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <v-simple-table v-if="section.items.length" dense>
            <tbody>
              <tr v-for="(item, i) in section.items" :key="i">
                <!-- Regelwerk-Auszüge über die volle Breite, jede Ziffer ein eigener Absatz wie im Sporthandbuch -->
                <td v-if="item.type === 'rules'" colspan="2" class="py-2">
                  <div class="text--secondary mb-1">{{ item.label }}</div>
                  <p v-for="(rule, j) in item.value" :key="j" class="text--primary rule mb-3">
                    <strong v-if="rule.nummer || rule.titel" class="d-block">{{ [rule.nummer, rule.titel].filter(Boolean).join(" ") }}</strong>
                    {{ rule.text }}
                    <span v-if="rule.quelle" class="d-block text-caption text--secondary">{{ rule.quelle }}</span>
                  </p>
                </td>
                <td v-if="item.type !== 'rules'" class="text--secondary label-col">{{ item.label }}</td>
                <td v-if="item.type !== 'rules'" class="py-2">
                  <template v-if="item.type === 'tags'">
                    <v-chip v-for="tag in item.value" :key="tag" x-small outlined class="mr-1 my-1">{{ tag }}</v-chip>
                  </template>
                  <template v-else>
                    <template v-for="(part, j) in linkParts(item.value)">
                      <a v-if="part.url" :key="j" :href="part.text" target="_blank" rel="noopener" class="text-break">{{ part.text }}</a>
                      <span v-else :key="j">{{ part.text }}</span>
                    </template>
                  </template>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>

        <!-- Persönlicher Kontext -->
        <v-card v-if="sheet.personal" outlined class="mt-4">
          <v-card-title class="subtitle-2 grey lighten-4 py-2">
            <v-icon small left>mdi-account</v-icon>
            Für dich
          </v-card-title>
          <v-card-text class="pt-3">
            <div class="text-caption text--secondary">Meine Waffen für diese Disziplin</div>
            <div v-if="sheet.personal.weapons.length" class="d-flex flex-wrap chip-gap mt-1">
              <v-chip v-for="w in sheet.personal.weapons" :key="w.id" small>
                <v-icon x-small left>mdi-pistol</v-icon>{{ w.label }}<span v-if="w.kaliber" class="ml-1 text--secondary">({{ w.kaliber }})</span>
              </v-chip>
            </div>
            <div v-else class="text--primary">Keine Waffe zugeordnet</div>

            <div class="text-caption text--secondary mt-3">Mein Schießbuch</div>
            <div class="text--primary">
              <template v-if="sheet.personal.shootingbook.count">
                {{ sheet.personal.shootingbook.count }} {{ sheet.personal.shootingbook.count === 1 ? 'Eintrag' : 'Einträge' }},
                zuletzt am {{ formatDate(sheet.personal.shootingbook.last) }}
              </template>
              <template v-else>Noch keine Einträge in dieser Disziplin</template>
            </div>
          </v-card-text>
        </v-card>

        <!-- Ähnliche Disziplinen -->
        <v-card v-if="sheet.related.length" outlined class="mt-4">
          <v-card-title class="subtitle-2 grey lighten-4 py-2">
            <v-icon small left>mdi-vector-link</v-icon>
            Ähnliche Disziplinen
          </v-card-title>
          <v-card-text class="pt-3 d-flex flex-wrap chip-gap">
            <v-chip v-for="r in sheet.related" :key="r.id" small outlined @click="$emit('select', r.id)">
              <strong class="mr-1">{{ r.nummer }}</strong>
              <span class="related-name">{{ r.name }}</span>
              <span class="ml-1 text--secondary">· {{ r.verband }}</span>
            </v-chip>
          </v-card-text>
        </v-card>
      </v-card-text>
    </template>

    <v-card-text v-else-if="!loading" class="text-center text--secondary py-12">
      Wähle links eine Disziplin aus.
    </v-card-text>
  </v-card>
</template>

<script>
// Auslaufdatum (JJJJ-MM-TT) liegt vor heute
const isExpired = (date) => !!date && date < new Date().toLocaleDateString("sv-SE");

const SECTION_ICONS = {
  waffe: "mdi-pistol",
  ablauf: "mdi-timer-outline",
  scheiben: "mdi-bullseye",
  ausruestung: "mdi-safety-goggles",
  teilnahme: "mdi-account-group",
  hinweise: "mdi-information-outline",
  quelle: "mdi-book-open-variant",
};

export default {
  name: "DisciplineSheet",
  props: {
    sheet: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    showBack: { type: Boolean, default: false },
  },
  computed: {
    levelColor() {
      return { 3: "success", 2: "orange", 1: "grey" }[this.sheet.completeness.level];
    },
    // Datum aus dem Dokument; ob es der erste Tag ohne oder der letzte mit ist, steht ggf. unter Hinweise
    expired() {
      return isExpired(this.sheet.auslauf_datum);
    },
    // Kennzahlen auf breiten Schirmen in einer Zeile
    factCols() {
      return Math.max(2, Math.floor(12 / Math.max(1, this.sheet.key_facts.length)));
    },
  },
  methods: {
    sectionIcon(key) {
      return SECTION_ICONS[key] || "mdi-text";
    },
    // Text in normale Teile und Links zerlegen (ohne v-html).
    // Satzzeichen am Ende ("…/507/," oder "…/518/).") gehören nicht zum Link.
    linkParts(text) {
      return String(text)
        .split(/(https?:\/\/\S*[^\s.,;:!?)\]"'“”»])/)
        .filter(Boolean)
        .map((part) => ({ text: part, url: /^https?:\/\//.test(part) }));
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value.replace(" ", "T")).toLocaleDateString("de-DE");
    },
  },
};
</script>

<style scoped>
.sheet-title {
  line-height: 1.3;
}
.chip-gap {
  gap: 6px;
}
.label-col {
  width: 35%;
  min-width: 120px;
  vertical-align: top;
  padding-top: 8px !important;
}
.rule {
  font-size: 0.875rem;
  line-height: 1.5;
}
.related-name {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
